import { getApiErrorMessage, useAuth } from '@milliswift/auth'
import {
  Button,
  CheckboxField,
  Divider,
  Field,
  Input,
  OTPInput,
  useToast,
} from '@milliswift/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { KeyRound, Mail, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation } from 'react-router-dom'
import { AuthCard, AuthPage } from '../components/AuthCard'
import { loginSchema, type LoginValues } from '../lib/schemas'

/**
 * Sign-in. Credentials first; if the account has MFA the form is replaced by
 * a code prompt rather than stacked on top of it, because a user mid-MFA has
 * exactly one task.
 */
export default function Login({ postLoginTarget }: { postLoginTarget: string }) {
  const { login, verifyMfa, pendingMfaToken } = useAuth()
  const toast = useToast()
  const { search } = useLocation()

  // A completed reset or verification bounces here with a banner flag.
  const showResetBanner = new URLSearchParams(search).get('reset') === 'true'
  const showVerifiedBanner = new URLSearchParams(search).get('verified') === 'true'

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', remember_me: false },
  })

  const [otp, setOtp] = useState('')
  const [mfaBusy, setMfaBusy] = useState(false)

  const submit = form.handleSubmit(async (values) => {
    try {
      const result = await login(values.email, values.password)
      if (!result.mfaRequired) {
        window.location.href = postLoginTarget
      }
      // MFA required: the step below takes over.
    } catch (error) {
      form.setError('password', { message: getApiErrorMessage(error) })
    }
  })

  const submitOtp = async (code: string) => {
    if (code.length !== 6 || mfaBusy) return
    setMfaBusy(true)
    try {
      await verifyMfa(code)
      window.location.href = postLoginTarget
    } catch (error) {
      toast.error('That code did not work', getApiErrorMessage(error))
      setOtp('')
      setMfaBusy(false)
    }
  }

  // Focus and clear when the MFA step appears.
  useEffect(() => {
    if (pendingMfaToken) setOtp('')
  }, [pendingMfaToken])

  /* ---------------- MFA step ---------------- */

  if (pendingMfaToken) {
    return (
      <AuthPage>
        <AuthCard
          title="Two-factor verification"
          subtitle="Enter the 6-digit code from your authenticator app."
          footer={
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="font-medium text-accent underline-offset-4 hover:underline"
            >
              Use a different account
            </button>
          }
        >
          <div className="space-y-6">
            <div className="flex justify-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sunken">
                <ShieldCheck className="h-6 w-6 text-accent" aria-hidden />
              </span>
            </div>

            <OTPInput
              value={otp}
              onValueChange={setOtp}
              onComplete={(code) => void submitOtp(code)}
              autoFocus
              className="justify-center"
              aria-label="Two-factor code"
              disabled={mfaBusy}
            />

            <Button
              className="w-full"
              onClick={() => void submitOtp(otp)}
              loading={mfaBusy}
              disabled={otp.length !== 6}
            >
              Verify and sign in
            </Button>

            <p className="text-center text-caption text-muted">
              Lost access?{' '}
              <Link to="/forgot-password" className="text-accent underline-offset-4 hover:underline">
                Recover your account
              </Link>
            </p>
          </div>
        </AuthCard>
      </AuthPage>
    )
  }

  /* ---------------- credentials step ---------------- */

  return (
    <AuthPage>
      <AuthCard
        title="Sign in"
        subtitle="Welcome back."
        footer={
          <>
            Need an account?{' '}
            <Link to="/signup" className="font-medium text-accent underline-offset-4 hover:underline">
              Create one
            </Link>
          </>
        }
      >
        <div className="space-y-4">
          {showResetBanner ? (
            <p className="rounded-lg bg-success-soft px-3 py-2 text-caption text-success-text">
              Password updated. Sign in with your new password.
            </p>
          ) : null}
          {showVerifiedBanner ? (
            <p className="rounded-lg bg-success-soft px-3 py-2 text-caption text-success-text">
              Email verified. Sign in to continue.
            </p>
          ) : null}

          <form onSubmit={submit} className="space-y-4" noValidate>
            <Field label="Email" required error={form.formState.errors.email?.message}>
              <Input
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                leading={<Mail className="h-4 w-4" />}
                {...form.register('email')}
              />
            </Field>

            <Field
              label="Password"
              required
              error={form.formState.errors.password?.message}
              labelSuffix={
                <Link
                  to="/forgot-password"
                  className="text-caption font-medium text-accent underline-offset-4 hover:underline"
                >
                  Forgot?
                </Link>
              }
            >
              <Input
                type="password"
                autoComplete="current-password"
                placeholder="••••••••••••"
                leading={<KeyRound className="h-4 w-4" />}
                {...form.register('password')}
              />
            </Field>

            <CheckboxField label="Keep me signed in" {...form.register('remember_me')} />

            <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
              Sign in
            </Button>
          </form>

          <Divider label="or" />
          <Button variant="outline" className="w-full" onClick={() => (window.location.href = '/signup')}>
            Create an account
          </Button>
        </div>
      </AuthCard>
    </AuthPage>
  )
}
